// Comprehensive Vermont family law data based on Custody X Change and official Vermont sources
const vermontData = {
  'vermont': {
    name: 'Vermont',
    overview: {
      keyPoints: [
        'Vermont follows the "best interests of the child" standard with specific factors outlined in Vermont Statutes Title 15 § 665',
        'Joint custody is not presumed - courts make individualized determinations based on evidence and circumstances',
        'Vermont uses "parental rights and responsibilities" terminology instead of traditional custody language',
        'The state requires detailed parenting plans in all contested custody cases involving minor children',
        'Vermont has a strong emphasis on maintaining meaningful relationships between children and both parents',
        'The state recognizes domestic partnerships and same-sex marriages with full parental rights',
        'Vermont courts emphasize mediation and collaborative approaches to resolve family disputes'
      ],
      uniqueFeatures: [
        'Vermont was the first state to recognize civil unions and has progressive family law policies',
        'The state uses "parental rights and responsibilities" instead of custody terminology',
        'Vermont has strong provisions for LGBTQ+ families and non-traditional family structures',
        'The state allows for parent-child contact orders rather than traditional visitation',
        'Vermont has specific provisions for families in rural communities with limited resources',
        'The state recognizes the importance of extended family and community connections',
        'Vermont has integrated family court system handling all family-related matters'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in Vermont for at least 6 months before filing, or Vermont must be the child\'s home state under UCCJEA',
      filing: 'File in Family Court in the county where either parent resides or where the child has lived for the past 6 months',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, court order, or presumption of paternity',
      documentation: 'Required documents include complaint, summons, financial affidavit, and proposed parenting plan in contested cases',
      uniqueRules: [
        'Vermont requires financial disclosure in all family law cases involving children or support',
        'Mediation is strongly encouraged and may be ordered by the court before contested hearings',
        'Guardian ad litem appointment is discretionary based on case circumstances and child welfare',
        'Parent coordination services are available to help high-conflict families',
        'Vermont allows emergency orders for situations involving immediate child welfare concerns'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Vermont uses "physical responsibility" to describe where and when the child spends time with each parent',
      legalCustody: 'Called "legal responsibility" - involves decision-making authority for major life decisions including education, healthcare, and religious upbringing',
      jointCustody: 'Shared parental rights and responsibilities require court approval and demonstration that it serves the child\'s best interests',
      visitation: 'Called "parent-child contact" - includes detailed schedules for when the child is with each parent',
      specialProvisions: [
        'Supervised parent-child contact may be ordered when child safety is a concern',
        'Flexible contact arrangements are encouraged for cooperative parents',
        'Extended summer contact is standard to accommodate school schedules',
        'Holiday contact typically alternates between parents on annual basis',
        'Virtual contact (video calls) is increasingly recognized for long-distance situations',
        'Grandparent and extended family contact rights are protected under Vermont law'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed parental rights and responsibilities schedule including regular rotation, weekends, and holidays',
        'Specific holiday and vacation schedules with clear alternating arrangements between parents',
        'Transportation arrangements and responsibility for travel costs between parents\' residences',
        'Communication methods between parents and guidelines for parent-child contact',
        'Decision-making authority allocation for education, healthcare, religious, and extracurricular activities',
        'Dispute resolution procedures including mediation requirements before court intervention',
        'Relocation provisions and notification requirements for moves affecting the child\'s residence',
        'Any special conditions, restrictions, or accommodations for either parent',
        'Provisions for child care during work hours and right of first refusal',
        'Guidelines for introducing new romantic partners to the children',
        'Emergency contact procedures and decision-making protocols'
      ],
      tips: [
        'Be extremely specific about pickup and drop-off times, locations, and procedures to avoid confusion',
        'Include detailed provisions for Vermont\'s rural geography and seasonal weather conditions',
        'Address how to handle missed contact due to weather, illness, work, or emergencies',
        'Include specific language about meeting locations and travel considerations in rural Vermont',
        'Specify how major decisions will be made and communicated between parents',
        'Address the child\'s participation in extracurricular activities and school events',
        'Include technology use guidelines and virtual contact provisions for families in remote areas',
        'Plan for extended summer arrangements and accommodate Vermont\'s seasonal tourism economy'
      ]
    },
    childSupport: {
      guidelines: 'Vermont uses a modified Income Shares Model based on both parents\' gross income with Vermont-specific guidelines',
      factors: [
        'Gross monthly income of both parents from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Parent-child contact time and overnight stays with paying parent (if significant)',
        'Standard of living the child would have enjoyed if parents remained together',
        'Special needs of the child requiring additional financial support',
        'Vermont\'s cost of living and seasonal employment factors'
      ],
      calculation: 'Vermont Child Support Guidelines using Income Shares Model with state-specific support schedule',
      modifications: 'Real and substantial change in circumstances required, typically 15% change in support amount or significant change in income or circumstances',
      enforcement: 'Strong enforcement through Vermont Office of Child Support including wage garnishment, asset seizure, tax intercept, and license suspension',
      detailedInfo: {
        basicCalculation: {
          description: 'Vermont follows a modified Income Shares Model that estimates what parents would spend on their children if the family were intact, with adjustments for Vermont\'s unique economic factors.',
          steps: [
            'Calculate each parent\'s gross monthly income from all verifiable sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Calculate adjusted gross monthly income for each parent after allowable deductions',
            'Add both parents\' adjusted gross incomes to determine combined monthly income',
            'Use Vermont Child Support Guidelines Schedule to find basic support obligation for number of children',
            'Allocate support obligation proportionally based on each parent\'s percentage of combined income',
            'Add costs for health insurance, childcare, and extraordinary medical expenses',
            'Apply any adjustments for extended parent-child contact time or special circumstances',
            'Calculate final monthly support amount payable by obligor parent'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Seasonal employment income (common in Vermont tourism and agriculture)',
            'Spousal support or maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Royalties, patents, and intellectual property income',
            'Business income from partnerships, corporations, or LLCs'
          ],
          excluded: [
            'Means-tested public assistance (Reach Up, 3SquaresVT, etc.)',
            'Foster care payments received for non-biological children',
            'Child support received for other children not subject to current order',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or cohabitating partner',
            'Reimbursement for job-related expenses',
            'Income from illegal activities'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status',
            'Vermont state income taxes',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal support/maintenance to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child (added separately to support)',
            'Childcare expenses necessary for employment or education',
            'Extraordinary medical expenses for the child (shared proportionally)'
          ]
        },
        contactTimeAdjustment: {
          description: 'Vermont allows adjustment to child support when the paying parent has significant overnight parent-child contact time.',
          thresholds: [
            'No adjustment for less than 25% overnight contact (91 nights per year)',
            'Potential adjustment for 25-35% overnight contact (91-127 nights per year)',
            'Standard adjustment consideration for 35%+ overnight contact (128+ nights per year)',
            'Extended contact adjustment may apply for equal or near-equal time arrangements'
          ],
          calculation: 'Adjustment reduces support obligation based on additional direct costs during extended contact and reduced costs to primary parent',
          factors: [
            'Actual overnight stays documented over previous 12 months',
            'Additional housing costs for accommodating child during contact periods',
            'Duplicate expenses (clothing, toys, school supplies) in both homes',
            'Transportation costs for contact exchanges in rural Vermont',
            'Reduced household expenses for primary parent during contact periods'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically attributable to covering the child',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be reasonable in cost, accessible to child, and provide adequate coverage',
            considerations: [
              'Premium cost must be specifically for child coverage, not family plan differential',
              'Insurance must be available through employer or Vermont Health Connect marketplace',
              'Coverage should include medical, dental, and vision if reasonably available',
              'Both parents should have access to insurance cards and provider information',
              'Special considerations for rural healthcare access in Vermont'
            ]
          },
          childcare: {
            description: 'Necessary childcare costs directly related to employment, job training, or education',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be work-related or education-related necessity for custodial parent',
            considerations: [
              'Childcare must be necessary for parent to work or attend school',
              'Costs should be reasonable for Vermont area and type of care',
              'Before and after school care is typically included',
              'Summer childcare during school breaks may be included',
              'Receipts and documentation required for verification',
              'Rural childcare availability and costs considered'
            ]
          },
          extraordinaryMedical: {
            description: 'Uninsured medical, dental, and vision expenses exceeding $100 per year per child',
            examples: [
              'Dental treatment not covered by insurance (fillings, crowns, extractions)',
              'Orthodontic treatment and appliances',
              'Vision care including eye exams, glasses, and contact lenses',
              'Mental health counseling and therapy sessions',
              'Physical therapy and rehabilitation services',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies (braces, wheelchairs, etc.)',
              'Treatment for chronic conditions requiring ongoing care',
              'Emergency medical treatment and hospitalization',
              'Specialized medical care and consultations',
              'Travel expenses for medical care in rural areas'
            ],
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            procedure: 'Parent incurring expense should seek pre-approval for non-emergency expenses over $300'
          },
          extracurricular: {
            description: 'Costs for children\'s activities, sports, and enrichment programs',
            examples: [
              'Youth sports team fees, equipment, and travel costs',
              'Music lessons and instrument rental or purchase',
              'Dance, martial arts, gymnastics, or other activity classes',
              'Summer camps and recreational day programs',
              'School trips, field trips, and educational activities',
              'Tutoring and educational support services',
              'Art, theater, or other creative programs',
              'Environmental and outdoor education programs common in Vermont'
            ],
            note: 'Not automatically included in basic support calculation - must be agreed upon by parents or specifically court-ordered',
            considerations: [
              'Activities should be in the child\'s best interest and age-appropriate',
              'Costs should be reasonable and within family\'s financial means',
              'Both parents should have input on major extracurricular decisions',
              'Transportation to activities should be considered in rural Vermont'
            ]
          }
        },
        modifications: {
          grounds: [
            'Real and substantial change in either parent\'s income (typically 15% or more)',
            'Significant change in parent-child contact schedule or arrangement',
            'Change in health insurance costs or availability',
            'Change in childcare expenses or needs',
            'Child\'s needs change significantly (special needs, medical conditions)',
            'Emancipation, marriage, or death of a child',
            'Three years have passed since last order (allows for cost-of-living review)',
            'Change in number of children covered by order',
            'Change in employment circumstances or seasonal work patterns'
          ],
          process: [
            'File Motion to Modify Child Support with supporting documentation',
            'Serve the other parent with motion and updated financial affidavit',
            'Provide current income verification and expense documentation',
            'Attend court hearing or mediation as scheduled by family court',
            'Present evidence supporting the requested modification',
            'Obtain modified court order with new support amount'
          ],
          timeline: 'Modifications are typically effective from the date of filing the motion, not the date of the final court order',
          documentation: [
            'Updated financial affidavit with current income and expenses',
            'Recent pay stubs, tax returns, and employment verification',
            'Documentation of changed circumstances (medical bills, job loss, etc.)',
            'Current childcare and health insurance costs',
            'Evidence of parent-child contact schedule changes if applicable'
          ]
        },
        enforcement: {
          methods: [
            'Income withholding and automatic wage garnishment',
            'Interception of tax refunds (federal and state)',
            'Asset seizure including bank accounts and personal property',
            'Liens on real estate and personal property',
            'Suspension of driver\'s license and professional licenses',
            'Suspension of hunting, fishing, and recreational licenses',
            'Credit bureau reporting of delinquent support',
            'Contempt of court proceedings with possible jail time',
            'Passport denial for arrearages exceeding $2,500',
            'Interception of lottery winnings and gaming proceeds',
            'Garnishment of unemployment and workers\' compensation benefits'
          ],
          interest: 'Unpaid child support accrues interest at the rate of 12% per annum on past-due amounts',
          statute: 'No statute of limitations exists for collecting past-due child support - it remains collectible indefinitely',
          resources: [
            'Vermont Office of Child Support',
            'Local state\'s attorney offices for contempt proceedings',
            'Private collection agencies contracted by the state',
            'Interstate enforcement through other state agencies'
          ]
        }
      }
    },
    resources: {
      courts: [
        {
          name: 'Vermont Judiciary Family Division',
          url: 'https://www.vermontjudiciary.org/family-division',
          description: 'Official Vermont court system family division with forms, procedures, and self-help resources'
        },
        {
          name: 'Vermont Family Court Forms',
          url: 'https://www.vermontjudiciary.org/family-division/forms',
          description: 'Complete collection of Vermont family court forms for divorce, custody, and support cases'
        },
        {
          name: 'Vermont Self-Help Center',
          url: 'https://www.vermontjudiciary.org/self-help',
          description: 'Self-help resources and assistance for people representing themselves in family court'
        },
        {
          name: 'Chittenden County Family Court',
          address: '175 Main Street, Burlington, VT 05401',
          phone: '(802) 863-3467',
          description: 'Family court serving Burlington area and Chittenden County'
        },
        {
          name: 'Washington County Family Court',
          address: '255 North Main Street, Barre, VT 05641',
          phone: '(802) 828-2091',
          description: 'Family court serving Montpelier/Barre area and Washington County'
        },
        {
          name: 'Rutland County Family Court',
          address: '83 Center Street, Rutland, VT 05701',
          phone: '(802) 786-5880',
          description: 'Family court serving Rutland area and Rutland County'
        }
      ],
      support: [
        {
          name: 'Vermont Office of Child Support',
          url: 'https://dcf.vermont.gov/ocs',
          phone: '1-800-786-3214',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Vermont Legal Aid',
          url: 'https://www.vtlegalaid.org/',
          phone: '1-800-889-2047',
          description: 'Free legal services for low-income families in family law matters statewide'
        },
        {
          name: 'Vermont Bar Association Family Law Section',
          url: 'https://www.vtbar.org/sections/family-law/',
          description: 'Professional resources and directory of family law attorneys in Vermont'
        },
        {
          name: 'Vermont Network Against Domestic and Sexual Violence',
          url: 'https://www.vtnetwork.org/',
          phone: '1-800-228-7395',
          description: 'Statewide domestic violence resources and legal advocacy services'
        },
        {
          name: 'Vermont Fathers Rights Support Groups',
          url: 'https://www.facebook.com/groups/vermontfathersrights',
          description: 'Support networks and advocacy groups for fathers navigating family court in Vermont'
        }
      ],
      legal: [
        {
          name: 'Vermont Bar Association Lawyer Referral Service',
          url: 'https://www.vtbar.org/for-attorneys/lawyer-referral-service/',
          phone: '1-800-639-7036',
          description: 'Professional attorney referrals for family law cases throughout Vermont'
        },
        {
          name: 'Vermont Law School Legal Clinic',
          url: 'https://www.vermontlaw.edu/academics/clinics-and-externships',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Vermont Collaborative Law Professionals',
          url: 'https://www.collaborativelaw.org/find-a-professional/vermont/',
          description: 'Network of collaborative divorce attorneys and professionals in Vermont'
        },
        {
          name: 'Vermont Mediation Services',
          url: 'https://www.vtmediation.org/',
          description: 'Family mediation services and dispute resolution professionals statewide'
        },
        {
          name: 'Pro Bono Programs Vermont',
          url: 'https://www.vtbar.org/for-the-public/pro-bono/',
          description: 'Free and reduced-cost legal services for qualifying individuals'
        }
      ],
      educational: [
        {
          name: 'Parents Apart Program Vermont',
          url: 'https://www.vermontjudiciary.org/family-division/parent-education',
          description: 'Parent education program for divorcing parents with minor children (required in some cases)'
        },
        {
          name: 'Vermont Parenting Plan Resources',
          url: 'https://www.vermontjudiciary.org/family-division/parenting-plans',
          description: 'Comprehensive guide to creating effective parenting plans in Vermont'
        },
        {
          name: 'Vermont Guardian ad Litem Program',
          url: 'https://www.vermontjudiciary.org/family-division/guardian-ad-litem',
          description: 'Information about guardian ad litem services for children in contested custody cases'
        },
        {
          name: 'University of Vermont Extension - Family Life',
          url: 'https://www.uvm.edu/extension/youth-family',
          description: 'Educational programs for co-parenting, family relationships, and child development'
        },
        {
          name: 'Vermont Parent Information and Resource Centers',
          url: 'https://education.vermont.gov/student-support/vermont-family-network',
          description: 'Educational resources for parents navigating special needs and educational decisions'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'Chittenden County Family Court',
            address: '175 Main Street, Burlington, VT 05401',
            phone: '(802) 863-3467',
            jurisdiction: 'Chittenden County - Burlington, South Burlington, Winooski, and surrounding areas'
          },
          {
            name: 'Washington County Family Court',
            address: '255 North Main Street, Barre, VT 05641',
            phone: '(802) 828-2091',
            jurisdiction: 'Washington County - Montpelier, Barre, and central Vermont'
          },
          {
            name: 'Rutland County Family Court',
            address: '83 Center Street, Rutland, VT 05701',
            phone: '(802) 786-5880',
            jurisdiction: 'Rutland County - Rutland and southwestern Vermont'
          },
          {
            name: 'Windham County Family Court',
            address: '30 Putney Road, Brattleboro, VT 05301',
            phone: '(802) 257-2800',
            jurisdiction: 'Windham County - Brattleboro and southeastern Vermont'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$295.00',
          'Divorce (without children)': '$295.00',
          'Parental Rights and Responsibilities': '$154.00',
          'Child Support': '$154.00',
          'Modification': '$154.00',
          'Paternity': '$154.00',
          'Relief from Abuse Order': 'No fee'
        },
        requiredForms: [
          'Complaint for Divorce or Parental Rights and Responsibilities',
          'Summons',
          'Financial Affidavit',
          'Parenting Plan (if children involved)',
          'Child Support Guidelines Worksheet',
          'Certificate of Service'
        ]
      },
      statutes: [
        {
          name: 'Vermont Statutes Title 15 - Domestic Relations',
          url: 'https://legislature.vermont.gov/statutes/title/15',
          description: 'Primary statutes governing divorce, custody, and family law in Vermont'
        },
        {
          name: 'Vermont Child Support Guidelines (15 V.S.A. § 659)',
          url: 'https://legislature.vermont.gov/statutes/section/15/011/00659',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'Vermont Parental Rights and Responsibilities (15 V.S.A. § 665)',
          url: 'https://legislature.vermont.gov/statutes/section/15/011/00665',
          description: 'Comprehensive custody and parental rights laws for Vermont'
        }
      ],
      emergencyContacts: [
        {
          name: 'Vermont Domestic Violence Hotline',
          phone: '1-800-228-7395',
          description: '24/7 statewide domestic violence crisis line and support services'
        },
        {
          name: 'Vermont Child Abuse Hotline',
          phone: '1-800-649-5285',
          description: '24/7 child abuse and neglect reporting hotline'
        },
        {
          name: 'Vermont Crisis Text Line',
          phone: 'Text VT to 741741',
          description: '24/7 crisis support via text message'
        }
      ]
    },
    bestInterestsFactors: [
      'The relationship of the child with each parent and the ability and disposition of each parent to provide the child with love, affection, and guidance',
      'The ability and disposition of each parent to assure that the child receives adequate food, clothing, medical care, other material needs, and a safe environment',
      'The ability and disposition of each parent to meet the child\'s present and future developmental needs',
      'The quality of the adjustment of the child to the child\'s present housing, school, and community and the potential effect of any change',
      'The ability and disposition of each parent to foster a positive relationship and frequent and continuing contact with the other parent, including physical contact, except where contact will result in harm to the child or to a parent',
      'The quality of the child\'s relationship with the primary care provider, if appropriate',
      'The relationship of the child with any other person who may significantly affect the child',
      'The ability and disposition of the parents to communicate, cooperate with each other, and make joint decisions concerning the children where parental rights and responsibilities are to be shared or divided',
      'Evidence of abuse, as defined in section 1101 of this title, and the impact of the abuse on the child and on the relationship between the child and the abusing parent'
    ],
    practicalTips: [
      'Vermont courts value detailed, specific parenting plans that promote the child\'s best interests',
      'Document all interactions and communications with the other parent using email or text when possible',
      'Be prepared for mediation - Vermont courts strongly encourage cooperative resolution of disputes',
      'Keep detailed records of your parent-child contact time and any expenses related to the children',
      'Follow all court orders precisely - violations can seriously impact future parental rights decisions',
      'Consider hiring a local attorney familiar with Vermont family court procedures and judges',
      'Vermont\'s rural nature may require creative solutions for transportation and communication',
      'Focus on the children\'s needs and maintaining stability during the legal process'
    ]
  }
};

export default vermontData;